import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Box, Button, FileUpload, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import * as docx from 'docx-preview';
import { logger, handleError } from 'src/logger';
import { DocumentService, ALLOWED_EXTENSIONS_ACCEPT, ALLOWED_EXTENSIONS_DISPLAY } from '../../services/DocumentService';

interface DocumentUploaderProps {
  onFileSelected: (file: File) => void;
  onFormattedContentExtracted?: (htmlContent: string) => void;
  isProcessing?: boolean;
  error?: string;
  shouldRenderPreview?: boolean;
}

// File size limit constant
const MAX_FILE_SIZE_KB = 1000;

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onFileSelected,
  onFormattedContentExtracted,
  isProcessing = false,
  error,
  shouldRenderPreview = true
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [renderError, setRenderError] = useState<string>('');
  const [isRendering, setIsRendering] = useState(false);
  const [fileToRender, setFileToRender] = useState<File | null>(null);
  const [failedFile, setFailedFile] = useState<File | null>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const renderingOperationRef = useRef<boolean>(false);

  const handleFileChange = useCallback(
    async (files: File[]) => {
      // files is already an array at this point
      const file = files[0] || null;

      setSelectedFile(file);

      if (file) {
        try {
          // Use DocumentService for validation with KB limit converted to MB
          const validation = DocumentService.validateFile(file, MAX_FILE_SIZE_KB / 1024);
          if (!validation.isValid) {
            setRenderError(validation.error!);
            setSelectedFile(null); // Reset selection when validation fails
            logger.warn('File validation failed', {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              error: validation.error
            });
            return;
          }

          logger.info('Document file selected for processing', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type
          });

          onFileSelected(file);

          setRenderError('');
          setShowPreview(false);
          setIsRendering(true);

          if (viewerRef.current) {
            viewerRef.current.innerHTML = '';
          }

          setFileToRender(file);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

          setRenderError(errorMessage);
          setFailedFile(file); // Keep failed file for retry
          setSelectedFile(null); // Clear file from FileUpload component

          handleError('File processing failed', error);
        }
      }
    },
    [onFileSelected]
  );

  useEffect(() => {
    const renderDocument = async () => {
      if (!fileToRender || !viewerRef.current || !isRendering || !shouldRenderPreview) {
        return;
      }

      // Skip docx-preview rendering for image files - they don't need preview
      const lastDotIndex = fileToRender.name.lastIndexOf('.');
      const fileExtension = lastDotIndex > -1 ? fileToRender.name.slice(lastDotIndex + 1).toLowerCase() : '';
      const isImageFile = ['jpg', 'jpeg', 'png'].includes(fileExtension);

      if (isImageFile) {
        logger.info('Skipping preview rendering for image file', {
          fileName: fileToRender.name,
          fileType: fileToRender.type
        });
        setIsRendering(false);
        return; // Images don't need docx-preview rendering
      }

      // Mark operation as active
      renderingOperationRef.current = true;

      try {
        const blob = new Blob([fileToRender], { type: fileToRender.type });

        // Check if operation is still valid and viewer is available
        if (!viewerRef.current || !renderingOperationRef.current) {
          handleError('Document viewer became unavailable during rendering', new Error('ViewerRef lost or operation cancelled'));
          setRenderError('Document viewer became unavailable. Please try again.');
          setIsRendering(false);
          renderingOperationRef.current = false;
          return;
        }

        const renderOptions = {
          debug: false,
          className: 'docx'
        };

        const renderPromise = docx.renderAsync(blob, viewerRef.current, undefined, renderOptions);
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Rendering timeout after 30 seconds')), 30000));

        await Promise.race([renderPromise, timeoutPromise]);

        // Check if operation is still valid before proceeding with DOM updates
        if (!renderingOperationRef.current) {
          logger.info('Document rendering completed but operation was cancelled', {
            fileName: fileToRender.name
          });
          return;
        }

        logger.info('Document rendered successfully', {
          fileName: fileToRender.name,
          fileSize: fileToRender.size
        });

        // Only update DOM if operation is still valid and viewer exists
        if (onFormattedContentExtracted && viewerRef.current && renderingOperationRef.current) {
          const extractedHTML = viewerRef.current.innerHTML;
          onFormattedContentExtracted(extractedHTML);
        }

        // Only show preview if operation is still valid
        if (renderingOperationRef.current) {
          setShowPreview(true);
        }
      } catch (renderError) {
        // Only handle error if operation is still valid
        if (renderingOperationRef.current) {
          const errorMessage = renderError instanceof Error ? renderError.message : 'Unknown error';

          handleError('Document rendering failed', renderError);

          if (renderError instanceof Error && renderError.message.includes('timeout')) {
            setRenderError(`Document rendering timed out after 30 seconds. The file might be corrupted or too complex.`);
            logger.warn('Document rendering timed out - background operation may still be running', {
              fileName: fileToRender.name,
              fileSize: fileToRender.size
            });
          } else {
            setRenderError(`An error occurred rendering the document: ${errorMessage}`);
          }
        }
      } finally {
        // Always clean up operation tracking and rendering state
        renderingOperationRef.current = false;
        setIsRendering(false);
      }
    };

    renderDocument();
  }, [fileToRender, isRendering, shouldRenderPreview, onFormattedContentExtracted]);

  // Cleanup effect to cancel operations on component unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing rendering operation on component unmount
      renderingOperationRef.current = false;
      logger.info('DocumentUploader component unmounting - cancelling any ongoing rendering operations');
    };
  }, []);

  const handleRetry = useCallback(() => {
    if (failedFile) {
      setRenderError('');
      setFailedFile(null);
      handleFileChange([failedFile]);
    }
  }, [failedFile, handleFileChange]);

  const clearFiles = () => {
    // Cancel any ongoing rendering operation
    renderingOperationRef.current = false;

    setSelectedFile(null);
    setShowPreview(false);
    setRenderError('');
    setIsRendering(false);
    setFileToRender(null);
    setFailedFile(null);

    if (viewerRef.current) {
      viewerRef.current.innerHTML = '';
    }

    logger.info('File cleared and rendering operation cancelled');
  };

  return (
    <Box>
      <SpaceBetween direction="vertical" size="s">
        <FileUpload
          onChange={({ detail }: { detail: { value: File[] } }) => {
            // Prevent file changes during processing
            if (isProcessing) {
              return;
            }
            // Safely convert to array - handles both FileList and Array
            const files = detail.value || [];
            const filesArray = files instanceof FileList ? Array.from(files) : Array.isArray(files) ? files : [];
            handleFileChange(filesArray);
          }}
          value={selectedFile ? [selectedFile] : []}
          i18nStrings={{
            uploadButtonText: (multiple: boolean) => (isProcessing ? 'Processing...' : selectedFile ? 'Choose different file' : 'Choose file'),
            dropzoneText: (multiple: boolean) => (isProcessing ? 'Processing document...' : 'Drag file here to upload'),
            removeFileAriaLabel: (fileIndex: number) => `Remove file ${selectedFile?.name || 'selected file'}`,
            limitShowFewer: 'Show fewer files',
            limitShowMore: 'Show more files',
            errorIconAriaLabel: 'Error'
          }}
          multiple={false}
          accept={ALLOWED_EXTENSIONS_ACCEPT}
          showFileLastModified
          showFileSize
          showFileThumbnail
          constraintText={`Supported formats: ${ALLOWED_EXTENSIONS_DISPLAY} | Maximum File Size: ${MAX_FILE_SIZE_KB} KB`}
        />

        {/* Hidden viewer div for content extraction only - no visual preview */}
        <div ref={viewerRef} style={{ display: 'none' }}>
          {/* Document content will be rendered here by docx-preview for content extraction only */}
        </div>

        {/* Error Display with Retry Button */}
        {(error || renderError) && (
          <SpaceBetween direction="horizontal" size="xs">
            <StatusIndicator type="error">{error || renderError}</StatusIndicator>
            {failedFile && (
              <Button variant="link" iconName="refresh" onClick={handleRetry}>
                Retry upload
              </Button>
            )}
          </SpaceBetween>
        )}

        {/* Clear Button */}
        {selectedFile && !isProcessing && (
          <Box textAlign="right">
            <Button variant="link" onClick={clearFiles}>
              Clear selection
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </Box>
  );
};
